import { Card, Progress, Tag } from "antd";
import { Award, Clock, FileText, Target, TrendingUp } from "lucide-react";
import React from "react";
import AssessmentProgress from "./components/AssessmentProgress";

const ViewJobPost = ({ application }) => {
  return (
    <div className="flex-1 overflow-y-auto py-6 space-y-6 h-[65vh]">
      {application.nextStep && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-emerald-50 border border-purple-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-purple-900 mb-1">
                  <strong>Next Step:</strong>
                </p>
                <p className="text-sm text-purple-800">{application.nextStep.title}</p>
                <p className="text-xs text-purple-700 mt-1">{application.nextStep.scheduledFor}</p>
              </div>
            </div>
          </div>
        )}

        {application.assessmentScores?.length > 0 && (
          <div className="flex flex-col gap-6 rounded-xl p-6 border-2 border-purple-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-emerald-600 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-gray-900 text-lg font-medium">
                Overall Assessment Score
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {application.assessmentScores.map((score, index) => {
                const percent = score.value.split("/")[0] + "%";
                return (
                  <div
                    key={index}
                    className={`flex flex-col items-center p-4 rounded-lg ${
                      index === 0
                        ? "bg-gradient-to-br from-purple-50 to-purple-100"
                        : "bg-gray-50"
                    }`}
                  >
                    <p
                      className={`mb-1 ${
                        index === 0
                          ? "text-3xl label-gradient"
                          : "text-2xl text-gray-900"
                      }`}
                    >
                      {percent}
                    </p>
                    <p className="text-xs text-center text-gray-600">{score.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {application.detailedAssessments?.length > 0 && (
          <div className="bg-card flex flex-col gap-6 rounded-xl p-6 border-2 border-gray-200">
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-gray-900 font-medium">Test Results</h3>
            </div>

            <div className="space-y-6">
              {application.detailedAssessments.map((assessment, idx) => {
                const overallPercent = Math.round(
                  (assessment.totalScore / assessment.maxScore) * 100
                );

                return (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-gray-900 mb-1">
                          {assessment.title}
                        </h4>
                        <p className="text-xs text-gray-600">
                          Completed on {assessment.completedOn}
                        </p>
                      </div>

                      <span className="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-purple-600 to-emerald-600 text-white">
                        {assessment.totalScore}/{assessment.maxScore}
                      </span>
                    </div>

                    <Progress
                      percent={overallPercent}
                      strokeColor="#059669"
                      showInfo={true}
                    />

                    {assessment.breakdown?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                        <p className="text-sm text-gray-700">Section Breakdown:</p>

                        {assessment.breakdown.map((item, i) => {
                          const percent = Math.round(
                            (item.score / item.max) * 100
                          );

                          return (
                            <div key={i}>
                              <div className="flex flex-col  justify-between text-xs text-gray-700 mb-1">
                                <span>{item.name}</span>
                                <span className="flex items-center justify-between">
                                  <p>{item.score}/{item.max} </p>
                                  <p>{percent}%</p>
                                </span>
                              </div>

                              <Progress
                                percent={percent}
                                strokeColor="#059669"
                                showInfo={false}
                                size={"small"}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          </div>
        )}

       {application.skills?.length > 0 && (
        <div className="bg-card flex flex-col gap-6 rounded-xl p-6 border-2 border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-purple-600"/>
            </div>
            <h3 className="text-gray-900 font-medium">Skills Evaluation</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {application.skills.map((skill, index) => {
              const percent = Math.round((skill.level / 5) * 100);
              return (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-900">{skill.name}</span>
                    <span className="status-indicator normal"> Assessed</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress
                      percent={percent}
                      showInfo={false}
                      strokeWidth={8}
                      strokeColor={{
                        "0%": "#7c3aed",
                        "100%": "#10b981",
                      }}
                      className="flex-1"
                    />
                    <span className="text-xs text-gray-600">{skill.level}/5</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {application.overallFeedback?.trim() && (
        <div class="text-card-foreground flex flex-col gap-6 rounded-xl p-6 border-2 border-purple-100 bg-gradient-to-br from-purple-50/30 to-emerald-50/30">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 bg-gradient-to-r from-purple-600 to-emerald-600 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white"/>
            </div>
            <h3 class="text-gray-900 text-lg font-medium">Overall Feedback</h3>
          </div>
          <p class="text-gray-700 text-base">{application.overallFeedback}</p>
        </div>
      )}
      <Card className="border-2">
        <h4 className="text-lg mb-8 text-gray-800 font-medium">Assessment Progress Timeline</h4>
        {application.progress && <AssessmentProgress progress={application.progress} />}
      </Card>
    </div>
  );
};


export default ViewJobPost;
